import React from 'react';
import { useTranslation } from 'react-i18next';
import { Type as TypeIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

import { headingFont, TEXT_COMBOS, TEXT_STYLES, TextPresetId } from '../text-presets';
import type { BrandInfo, FontPair } from '../types';
import type { DesignEditorApi } from '../useDesignEditor';
import { SectionLabel } from '../ui';

/**
 * Text: one obvious way to add a box, the four styles every layout needs, and
 * the combos that are tedious to build by hand (eyebrow + headline lockup,
 * collection title, quote, spec and price tags, a CTA button, a slide number).
 * Previews are drawn in the pairing the vendor picked.
 */
export const TextPanel: React.FC<{ ed: DesignEditorApi; brand: BrandInfo; pair: FontPair }> = ({ ed, brand, pair }) => {
    const { t } = useTranslation();
    const head = headingFont({ brand, pair });
    const headStyle: React.CSSProperties = { fontFamily: `"${head}", serif`, fontWeight: head === pair.heading ? pair.headingWeight : 400 };
    const bodyStyle: React.CSSProperties = { fontFamily: `"${pair.body}", sans-serif` };

    const styleRow = (id: TextPresetId) => {
        const label = t(`DESIGN_EDITOR.PRESET_${id.toUpperCase()}`);
        const style: React.CSSProperties = id === 'headline'
            ? { ...headStyle, fontSize: 26, lineHeight: 1.1, textTransform: pair.headingUpper ? 'uppercase' : undefined }
            : id === 'subheading' ? { ...bodyStyle, fontSize: 17, fontWeight: 500 }
                : id === 'body' ? { ...bodyStyle, fontSize: 13.5 }
                    : { ...bodyStyle, fontSize: 11.5, letterSpacing: '0.04em', opacity: 0.75 };
        return (
            <button
                key={id}
                type="button"
                onClick={() => { void ed.addTextPreset(id); }}
                className="w-full cursor-pointer rounded-xl border bg-background px-3 py-3 text-start text-foreground transition-colors hover:border-foreground/40"
                style={style}
            >
                {label}
            </button>
        );
    };

    const comboPreview = (id: TextPresetId): React.ReactNode => {
        switch (id) {
            case 'lockup':
                return (
                    <div className="flex flex-col items-center gap-1">
                        <span className="text-[8px] tracking-[0.3em] uppercase" style={{ ...bodyStyle, fontWeight: 500 }}>{t('DESIGN_EDITOR.PRESET_EYEBROW_TEXT')}</span>
                        <span className="text-[17px] leading-none" style={headStyle}>{t('DESIGN_EDITOR.PRESET_LOCKUP_TEXT')}</span>
                    </div>
                );
            case 'collection':
                return (
                    <div className="flex flex-col items-center gap-1">
                        <span className="text-[19px] leading-none italic" style={headStyle}>{t('DESIGN_EDITOR.PRESET_COLLECTION_TEXT')}</span>
                        <span className="h-px w-5 bg-foreground" />
                        <span className="text-[7px] tracking-[0.34em] uppercase" style={{ ...bodyStyle, fontWeight: 500 }}>{t('DESIGN_EDITOR.PRESET_COLLECTION_SUB')}</span>
                    </div>
                );
            case 'quote':
                return <span className="px-2 text-center text-[12px] leading-snug italic" style={headStyle}>{t('DESIGN_EDITOR.PRESET_QUOTE_TEXT')}</span>;
            case 'spec':
                return <span className="rounded-full bg-foreground/[0.06] px-2.5 py-1 text-[10px] ring-1 ring-border" style={{ ...bodyStyle, fontWeight: 500 }}>{t('DESIGN_EDITOR.PRESET_SPEC_TEXT')}</span>;
            case 'price':
                return <span className="px-2.5 py-1 text-[12px]" style={{ ...bodyStyle, fontWeight: 500, background: brand.accent, color: brand.onAccent, borderRadius: 3 }}>{t('DESIGN_EDITOR.PRESET_PRICE_TEXT')}</span>;
            case 'cta':
                return <span className="rounded-full px-3 py-1.5 text-[10px]" style={{ ...bodyStyle, fontWeight: 500, background: brand.accent, color: brand.onAccent }}>{t('DESIGN_EDITOR.PRESET_CTA_TEXT')}</span>;
            case 'number':
            default:
                return <span className="text-[30px] leading-none" style={{ ...headStyle, color: brand.accent }}>01</span>;
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <button
                type="button"
                onClick={() => { void ed.addTextBox(); }}
                className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-foreground text-sm font-medium text-background transition-opacity hover:opacity-90 [&_svg]:size-4"
            >
                <TypeIcon />
                {t('DESIGN_EDITOR.ADD_TEXT_BOX')}
            </button>

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.TEXT_STYLES')}</SectionLabel>
                {TEXT_STYLES.map(styleRow)}
            </div>

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.TEXT_COMBOS')}</SectionLabel>
                <div className="grid grid-cols-2 gap-2">
                    {TEXT_COMBOS.map((id) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => { void ed.addTextPreset(id); }}
                            title={t(`DESIGN_EDITOR.PRESET_${id.toUpperCase()}`)}
                            className={cn(
                                'flex h-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-xl border bg-[#F6F4F0] p-2 text-[#1A1917] transition-colors hover:border-foreground/40',
                            )}
                        >
                            {comboPreview(id)}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
